import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ClientBankAccountsLists, IsTodayDate, Box, withStyles, toSupportedCurrency, getMaskedNumber } from 'vopay';

import { Title } from 'UI';
import { format } from 'date-fns';

import { useTranslation } from 'react-i18next';
import { useDataFetchIndicator } from '@/hooks';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useNavMenu } from '@/providers/NavMenu.provider';
import { useOpenModule } from '@/providers/OpenModule.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';

import useFetchClientAccountList from '@/hooks/useApi/useFetchClientAccountList';
import useFetchClientBankAccounts from '@/hooks/useApi/useFetchClientBankAccounts';
import useFundMyAccount from '@/hooks/useApi/useFundMyAccount';
import useFetchFundingOptions from '@/hooks/useApi/useFetchFundingOptions';
import usePostFundMyAccountSchedule from '@/hooks/useApi/usePostFundMyAccountSchedule';
import FundAccountForm from './FundAccountForm';
import { MyAccountFormData } from '../types';
import styles from '@/styles/PaymentAndCollection/PaymentAndCollection.module.scss';

const cn = withStyles(styles);

type LocationState = {
  currency?: string;
  type?: string;
};

export default function FundAccountPage() {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.FundMyAccount.Form' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { state } = useLocation<LocationState>();
  const { userSettings } = useUserSettingsProvider();
  const history = useHistory();
  const alertDialog = useAlertDialog();
  const [, setPending] = useDataFetchIndicator();

  const {
    response: { clientAccountsEnabled, Country },
  } = useClientData();
  const { getModuleTitle, setSubNavActiveModule } = useNavMenu();
  const { setOpenModule } = useOpenModule();

  const selectedCurrency = state?.currency ?? (Country === 'US' ? 'USD' : 'CAD');
  // createFunding hard-forces Source: 'bank' for client-accounts clients, so the Credit Card
  // preselect only applies outside that flow.
  const isCreditCardEntry = !clientAccountsEnabled && state?.type === 'Credit Card';

  const [, , , fetchBankAccounts] = useFetchClientBankAccounts({});
  const [, , , fetchClientAccounts] = useFetchClientAccountList({});
  const { fundMyAccount } = useFundMyAccount();
  const [, , , postFundMyAccountScheduleAsync] = usePostFundMyAccountSchedule({});
  const [selectedBank, setSelectedBank] = useState<ClientBankAccountsLists | undefined>();
  const { fetching: fetchingFundingOptions, fundingOptions, fetchFundingOptions } = useFetchFundingOptions();
  const bankFundingOption = fundingOptions.find((option) => option.Source === 'bank');
  const creditFundingOption = fundingOptions.find((option) => option.Source === 'credit');

  const [formData, setFormData] = useState<MyAccountFormData>({
    Currency: selectedCurrency,
    PaymentMethod: isCreditCardEntry ? 'Credit Card' : 'Bank Account',
    Account: isCreditCardEntry ? 'credit' : 'bank',
    Amount: '',
    ScheduleType: 'single',
    TakeFromAccountOn: String(Date.now()),
    Frequency: 'monthly',
    SemiMonthlyDates: '',
    StartDate: String(Date.now()),
    Ending: 'End Date',
    EndDate: '',
    Description: '',
    MessageForRecipient: '',
    Notes: `Fund via 'Fund My Account'`,
    ClientAccount: '',
    EndingAfterPayments: '',
    TransactionLabel: '',
    IdentificationNumber: '',
  });

  async function initFundingOptions(currency: string) {
    try {
      const res = await fetchFundingOptions({ Currency: currency });
      if (!res.Success) throw new Error(res.ErrorMessage);
    } catch (error) {
      alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : String(error));
    }
  }

  useEffect(() => {
    if (!clientAccountsEnabled) return;

    async function initClientAccount() {
      try {
        const clientAccountsRes = await fetchClientAccounts({});
        if (!clientAccountsRes.Success) throw new Error(clientAccountsRes.ErrorMessage);

        const primaryClientAccount = Object.values(clientAccountsRes.ClientAccounts || {}).find(
          (obj) => obj.IsPrimaryAccount === '1',
        );

        if (!primaryClientAccount) return;

        setFormData((prev) => ({ ...prev, ClientAccount: primaryClientAccount.ClientAccountID }));

        const bankAccountsRes = await fetchBankAccounts({
          ClientAccountID: primaryClientAccount.ClientAccountID,
          Timezone: userSettings.timezone,
        });

        if (!bankAccountsRes.Success) throw new Error(bankAccountsRes.ErrorMessage);

        const defaultBankAccount = Object.values(bankAccountsRes.BankAccounts || {}).find(
          (obj) => obj.IsDefault === '1',
        );

        if (!defaultBankAccount) {
          alertDialog.error(
            tGlobal('error'),
            t('ErrorMessage.noDefaultBankAccount', {
              defaultValue: 'There is no default bank account set up for client account ID: {{clientAccountID}}',
              clientAccountID: primaryClientAccount.ClientAccountID,
            }),
          );
        }

        setSelectedBank(defaultBankAccount);
      } catch (error) {
        alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : String(error));
      }
    }

    initClientAccount();
  }, []);

  const handleCurrencyChange = async (currency: string) => {
    if (clientAccountsEnabled) return;

    initFundingOptions(currency);
  };

  const createFunding = async (values: MyAccountFormData) => {
    const source = values.Account === 'bank' ? 'bank' : 'credit';
    const hasFundingSource = clientAccountsEnabled
      ? !!selectedBank
      : source === 'credit'
        ? !!creditFundingOption
        : !!bankFundingOption;

    if (!hasFundingSource) return;

    const amount = Number(String(values.Amount)?.replaceAll(',', ''));
    const scheduleType = values.ScheduleType || 'single';
    const takeFromAccountOn = values.TakeFromAccountOn || String(Date.now());
    const startDate = values.StartDate || String(Date.now());

    const accountQuery = new URLSearchParams({
      accountName:
        source === 'credit'
          ? creditFundingOption?.CreditCardHolderName || ''
          : clientAccountsEnabled
            ? selectedBank?.AccountHolderName || ''
            : bankFundingOption?.AccountHolderName || '',
      accountDetails:
        source === 'credit'
          ? getMaskedNumber(creditFundingOption?.CreditCardNumber || '')
          : clientAccountsEnabled
            ? selectedBank?.AccountNumber || ''
            : bankFundingOption?.AccountNumber || '',
      paymentMethod: source,
      fundingAmount: values.Amount || '',
    });

    const isImmediate = scheduleType === 'single' && IsTodayDate(format(Number(takeFromAccountOn), 'yyyy-MM-dd'));

    if (isImmediate) {
      try {
        setPending(true);
        const response = await fundMyAccount({
          Token: clientAccountsEnabled ? '' : selectedBank?.Token || '',
          Amount: amount,
          Currency: toSupportedCurrency(values.Currency),
          Notes: values.Notes || '',
          ClientAccountID: clientAccountsEnabled ? values.ClientAccount : '',
          Source: clientAccountsEnabled ? 'bank' : source,
        });

        setOpenModule('statements');
        setSubNavActiveModule('transactionsHistory');
        history.push(
          `/my-account-confirmation/${response.TransactionID}/${values.Currency || ''}/fund/transaction?${accountQuery}`,
        );
      } catch (error) {
        alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : String(error));
      } finally {
        setPending(false);
      }
    } else {
      try {
        setPending(true);
        const response = await postFundMyAccountScheduleAsync({
          Amount: amount,
          Frequency: scheduleType,
          NameOfFrequency: values.Frequency || undefined,
          SemiMonthlyFrequencyType: values.SemiMonthlyDates || '',
          ScheduleStartDate:
            scheduleType === 'recurring'
              ? format(Number(startDate), 'yyyy-MM-dd')
              : format(Number(takeFromAccountOn), 'yyyy-MM-dd'),
          ScheduleEndDate:
            values.Ending === 'End Date' && scheduleType === 'recurring'
              ? format(Number(values.EndDate), 'yyyy-MM-dd')
              : '',
          Currency: values.Currency || '',
          EndingAfterPayments: values.Ending === 'Payments' ? values.EndingAfterPayments || '' : '',
          Description: values.Description || '',
          Source: clientAccountsEnabled ? 'bank' : source,
        });

        if (!response.Success) throw new Error(response.ErrorMessage);

        setOpenModule('statements');
        history.push(
          `/my-account-confirmation/${response.ScheduledTransactionID}/${values.Currency || ''}/fund/scheduled?${accountQuery}`,
        );
      } catch (error) {
        alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : String(error));
      } finally {
        setPending(false);
      }
    }
  };

  return (
    <div className={cn('pageContainer')}>
      <div className={cn('stickyHeader')}>
        <Box jc="center" width="100%">
          <Box maxwidth="1121px" boxWidth="100%" p="md">
            <Title>{getModuleTitle()}</Title>
          </Box>
        </Box>
      </div>

      <Box jc="center" width="100%">
        <Box flx="col" maxwidth="1121px" boxWidth="100%" p="md">
          <FundAccountForm
            defaultValues={formData}
            onSubmit={createFunding}
            onCurrencyChange={handleCurrencyChange}
            hasBankAccount={clientAccountsEnabled ? !!selectedBank : !!bankFundingOption}
            hasCreditCard={!!creditFundingOption}
            fundingOptions={fundingOptions}
            fetchingFundingOptions={fetchingFundingOptions}
          />
        </Box>
      </Box>
    </div>
  );
}
